import React, { useEffect, useRef, useState } from "react";
import { useLocation, Redirect } from "react-router-dom";

import api from "../../services/api";
import GeraPix from "../../components/GeraPix";
import { criaPix } from "../../services/utils";
import { CLICKSIGN_ENDPOINT, BASE_URL } from "../../configs/consts";

import "./styles/global.css";
import { Mensagem, Container } from "./styles/styles";

// Assinatura Incorporada (embedded_signature / tokenless) da Clicksign API V3.
// NÃO usa iframe/widget: são 2 chamadas HTTP direto do navegador, com a nossa
// própria UI (baseado no noWidget.js de referência da Clicksign):
//   GET  <endpoint>/notarial/embedded_signature/signatures/<signerKey>/documents
//   POST <endpoint>/notarial/embedded_signature/signatures/<signerKey>/sign
// A "chave do signatário" é o id do signatário (signerKey). O endpoint NÃO usa
// o token da conta — a própria signerKey é o segredo. O navegador envia o
// header Origin automaticamente.
const LABELS = {
  name: "Nome completo",
  documentation: "CPF",
  birthday: "Data de nascimento",
};

function urlBaseAssinatura(signerKey) {
  return `${CLICKSIGN_ENDPOINT}/notarial/embedded_signature/signatures/${signerKey}`;
}

// ===========================================================================
// Componente reutilizável: assina UM contrato (principal ou baile) via noWidget.
// Mostra o PDF (previewUrl, servido inline pela nossa API), pede os campos
// obrigatórios e assina. Avisa o pai quando concluir (onConcluido) ou quando
// não conseguir carregar o documento (onErroFatal).
// ===========================================================================
function ContratoSign({
  signerKey,
  pollEnvelopeId,
  previewUrl,
  titulo,
  subtitulo,
  botaoLabel,
  onConcluido,
  onErroFatal,
}) {
  const [carregandoDocs, setCarregandoDocs] = useState(true);
  const [documentKeys, setDocumentKeys] = useState([]);
  const [requiredFields, setRequiredFields] = useState({});
  const [form, setForm] = useState({});
  const [assinando, setAssinando] = useState(false);
  const [erroAssinatura, setErroAssinatura] = useState(null);
  const pollRef = useRef(null);

  // polling do status no backend (em dev o /contrato/status faz refresh ao vivo
  // na Clicksign; em prod o webhook já terá atualizado a linha). Apenas best-effort.
  function iniciarPolling() {
    if (!pollEnvelopeId || pollRef.current) return;
    let tentativas = 0;
    pollRef.current = setInterval(async () => {
      tentativas += 1;
      try {
        const { data } = await api.get(`/contrato/status/${pollEnvelopeId}`);
        if (
          data &&
          data.status &&
          ["closed", "canceled", "refused", "expired"].indexOf(data.status) !==
            -1
        ) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        /* silencioso: tenta de novo no próximo ciclo */
      }
      if (tentativas >= 30 && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 4000);
  }

  // busca os documentos do envelope (e os campos que o signatário precisa
  // preencher) assim que a tela abre com uma chave de signatário válida.
  useEffect(() => {
    let cancelado = false;
    if (!signerKey) {
      setCarregandoDocs(false);
      if (onErroFatal) onErroFatal();
      return undefined;
    }

    (async () => {
      try {
        const resp = await fetch(`${urlBaseAssinatura(signerKey)}/documents`, {
          headers: { "Content-Type": "application/json" },
        });
        const texto = await resp.text();
        const dados = texto ? JSON.parse(texto) : {};
        if (cancelado) return;
        if (!dados.documents) {
          throw new Error(
            (dados.error && dados.error.message) || "sem documentos"
          );
        }
        setDocumentKeys(dados.documents.map((d) => d.key));
        setRequiredFields(dados.required_fields || {});
      } catch (e) {
        if (!cancelado && onErroFatal) onErroFatal();
      } finally {
        if (!cancelado) setCarregandoDocs(false);
      }
    })();

    return () => {
      cancelado = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signerKey]);

  const camposObrigatorios = Object.keys(requiredFields).filter(
    (f) => requiredFields[f]
  );
  const podeAssinar =
    !assinando &&
    documentKeys.length > 0 &&
    camposObrigatorios.every((f) => form[f]);

  async function assinar() {
    if (!podeAssinar) return;
    setAssinando(true);
    setErroAssinatura(null);

    const signer = {};
    camposObrigatorios.forEach((f) => {
      signer[f] = form[f];
    });

    try {
      const resp = await fetch(`${urlBaseAssinatura(signerKey)}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signature: { signer, document_keys: documentKeys },
        }),
      });
      const texto = await resp.text();
      // 200 com corpo vazio = sucesso (padrão da Clicksign)
      if (resp.status === 200 && !texto) {
        iniciarPolling();
        if (onConcluido) onConcluido();
        return;
      }
      const dados = texto ? JSON.parse(texto) : {};
      if (dados.status === "error") {
        throw dados.error || new Error("Falha ao assinar.");
      }
      // qualquer outra resposta 2xx com corpo: também tratamos como assinado
      iniciarPolling();
      if (onConcluido) onConcluido();
    } catch (e) {
      setErroAssinatura(
        (e && e.message) ||
          "Não foi possível assinar agora. Confira os dados e tente novamente."
      );
    } finally {
      setAssinando(false);
    }
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1040,
        alignSelf: "center",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h1>{titulo}</h1>
        <p style={{ color: "#666" }}>{subtitulo}</p>
      </div>

      {carregandoDocs ? (
        <p>Carregando contrato…</p>
      ) : (
        <>
          {previewUrl && (
            <iframe
              title="Contrato"
              src={previewUrl}
              style={{
                width: "100%",
                height: "72vh",
                border: "1px solid #ddd",
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
          )}

          {previewUrl && (
            <p style={{ textAlign: "center", margin: "4px 0 18px" }}>
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0167ce", fontWeight: "bold" }}
              >
                📄 Abrir o contrato em nova aba
              </a>
            </p>
          )}

          <div style={{ width: "100%", maxWidth: 480 }}>
            {camposObrigatorios.map((campo) => (
              <div key={campo} style={{ margin: "10px 0" }}>
                <label style={{ display: "block", fontWeight: "bold" }}>
                  {LABELS[campo] || campo}
                </label>
                <input
                  type={campo === "birthday" ? "date" : "text"}
                  value={form[campo] || ""}
                  onChange={(e) =>
                    setForm({ ...form, [campo]: e.target.value })
                  }
                  style={{ width: "100%", padding: 8, boxSizing: "border-box" }}
                />
              </div>
            ))}

            {erroAssinatura && (
              <p style={{ color: "#c0392b" }}>{erroAssinatura}</p>
            )}

            <button
              type="button"
              onClick={assinar}
              disabled={!podeAssinar}
              style={{
                width: "100%",
                padding: 12,
                marginTop: 8,
                cursor: podeAssinar ? "pointer" : "not-allowed",
                opacity: podeAssinar ? 1 : 0.6,
              }}
            >
              {assinando ? "Assinando…" : botaoLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Etapa OPCIONAL do baile: o formando lê o contrato do baile e escolhe assinar
// (participar) ou pular. O envelope Clicksign do baile é criado SOB DEMANDA
// (POST /contrato/baile) só quando ele decide participar — não gera envelopes
// órfãos para quem pula. previewUrl é por formatura (a API serve o PDF inline).
// ===========================================================================
function EtapaBaile({ formId, principalEnvelopeId, onConcluido, onPular }) {
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState(null);
  const [assinaturaBaile, setAssinaturaBaile] = useState(null); // { envelopeId, signerKey }
  const previewUrl = `${BASE_URL}/contrato/baile/pdf/${formId}`;

  async function participar() {
    setCriando(true);
    setErro(null);
    try {
      const { data } = await api.post("/contrato/baile", {
        envelopeId: principalEnvelopeId,
      });
      if (!data || !data.signerKey) {
        throw new Error("Resposta inválida ao preparar o contrato do baile.");
      }
      setAssinaturaBaile({
        envelopeId: data.envelopeId,
        signerKey: data.signerKey,
      });
    } catch (e) {
      setErro(
        "Não foi possível preparar o contrato do baile agora. Tente novamente ou prossiga sem o baile."
      );
    } finally {
      setCriando(false);
    }
  }

  // Já optou por participar -> assina o contrato do baile (mesmo fluxo noWidget).
  if (assinaturaBaile) {
    return (
      <ContratoSign
        signerKey={assinaturaBaile.signerKey}
        pollEnvelopeId={assinaturaBaile.envelopeId}
        previewUrl={previewUrl}
        titulo="Contrato do baile"
        subtitulo="Leia o contrato do baile abaixo e preencha seus dados para assinar."
        botaoLabel="Li e concordo — Assinar contrato do baile"
        onConcluido={onConcluido}
        onErroFatal={() =>
          setErro(
            "Não foi possível carregar o contrato do baile agora. Tente novamente ou prossiga sem o baile."
          )
        }
      />
    );
  }

  // Oferta: ler o contrato do baile e decidir.
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1040,
        alignSelf: "center",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h1>Sua formatura tem um baile! 🎉</h1>
        <p style={{ color: "#666" }}>
          A participação no baile é opcional e tem um contrato próprio. Leia
          abaixo e escolha se deseja participar.
        </p>
      </div>

      <iframe
        title="Contrato do baile"
        src={previewUrl}
        style={{
          width: "100%",
          height: "72vh",
          border: "1px solid #ddd",
          borderRadius: 4,
          marginBottom: 8,
        }}
      />
      <p style={{ textAlign: "center", margin: "4px 0 18px" }}>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#0167ce", fontWeight: "bold" }}
        >
          📄 Abrir o contrato do baile em nova aba
        </a>
      </p>

      {erro && (
        <p style={{ color: "#c0392b", maxWidth: 480, textAlign: "center" }}>
          {erro}
        </p>
      )}

      <div style={{ width: "100%", maxWidth: 480 }}>
        <button
          type="button"
          onClick={participar}
          disabled={criando}
          style={{
            width: "100%",
            padding: 12,
            marginTop: 8,
            cursor: criando ? "not-allowed" : "pointer",
            opacity: criando ? 0.6 : 1,
          }}
        >
          {criando ? "Preparando…" : "Quero participar — assinar contrato do baile"}
        </button>

        <button
          type="button"
          onClick={onPular}
          disabled={criando}
          style={{
            width: "100%",
            padding: 12,
            marginTop: 10,
            background: "transparent",
            border: "none",
            color: "#666",
            textDecoration: "underline",
            cursor: criando ? "not-allowed" : "pointer",
          }}
        >
          Não quero participar do baile
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Etapa OBRIGATÓRIA: escolha da condição de pagamento, após as assinaturas.
// Lista as condições da formatura filtradas pelo que foi assinado (baile?),
// aplica a escolhida (POST /contrato/condicao) e gera o cronograma. O disparo
// de pagamento por formato (cartão/boleto/pix) é a etapa seguinte (Fase B).
// ===========================================================================
const FORMATO_LABEL = {
  cartao: "Cartão",
  boleto: "Boleto",
  misto: "Boleto + Cartão",
  misto_pix: "Pix + Cartão",
  pix: "Pix",
};

function brl(v) {
  try {
    return Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(v);
  } catch (e) {
    return "R$ " + Number(v || 0).toFixed(2);
  }
}

function dataBR(iso) {
  if (!iso) return "";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

function resumoCondicao(c) {
  if (c.formato === "pix") {
    return `à vista via QR ou parcelado em até ${c.pixMax || 1}x`;
  }
  const linhas = c.preview || [];
  const bol = linhas.filter((l) => l.segmento === "boleto").length;
  const pix = linhas.filter((l) => l.segmento === "pix").length;
  const card = linhas.find((l) => l.segmento === "cartao");
  const partes = [];
  if (bol) partes.push(`${bol}x no boleto`);
  if (pix) partes.push(`${pix}x no pix`);
  if (card) partes.push(`cartão em até ${card.maxParcelas}x`);
  return partes.join(" + ") || "—";
}

// ===========================================================================
// Ações de pagamento por formato, após a condição aplicada (Fase B). Cartão vai
// direto pra Cielo (checkout DMZ); boleto é gerado/registrado sob demanda; pix
// mostra o QR (GeraPix) e persiste o código copia-e-cola para reconciliação.
// ===========================================================================
function PagamentoActions({ aluno, conta, parcelas, pix, onPronto }) {
  const pixCreds = pix || {};
  const cartao = parcelas.find((p) => p.segmento === "cartao");
  const boletos = parcelas.filter((p) => p.segmento === "boleto");
  const pixes = parcelas.filter((p) => p.segmento === "pix");

  const [cartaoLoad, setCartaoLoad] = useState(false);
  const [cartaoMsg, setCartaoMsg] = useState(null);
  const [boletoState, setBoletoState] = useState({});

  // persiste o código pix (mesmo que o GeraPix renderiza) p/ reconciliação
  useEffect(() => {
    pixes.forEach((p) => {
      try {
        const code = criaPix({
          chave: pixCreds.chave,
          mensagem: `Contrato ${conta}`,
          titular: pixCreds.titular,
          cidade: pixCreds.cidade,
          transacao: `${p.parcid}`,
          valor: p.valor,
        });
        api.post(`/contrato/pix/${p.parcid}`, { aluno, code }).catch(() => {});
      } catch (e) {
        /* ignora: o QR ainda aparece via GeraPix */
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const btn = {
    width: "100%",
    maxWidth: 480,
    padding: 12,
    marginTop: 10,
    cursor: "pointer",
  };

  async function pagarCartao() {
    if (!cartao || cartaoLoad) return;
    setCartaoLoad(true);
    setCartaoMsg(null);
    try {
      const { data } = await api.post("/contrato/checkout", {
        aluno,
        parcid: cartao.parcid,
      });
      if (data && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setCartaoMsg("Não foi possível abrir o checkout. Tente pelo portal.");
    } catch (e) {
      setCartaoMsg("Não foi possível abrir o checkout. Tente pelo portal.");
    } finally {
      setCartaoLoad(false);
    }
  }

  async function gerarBoleto(parcid) {
    setBoletoState((s) => ({ ...s, [parcid]: { load: true } }));
    try {
      const { data } = await api.post("/contrato/boleto", { aluno, parcid });
      const b = (data && data.boleto) || {};
      setBoletoState((s) => ({
        ...s,
        [parcid]: { linha: b.linhaDigitavel, url: b.urlBoleto },
      }));
    } catch (e) {
      const d = e && e.response && e.response.data;
      setBoletoState((s) => ({
        ...s,
        [parcid]: { err: (d && d.detalhe) || "Falha ao gerar o boleto." },
      }));
    }
  }

  return (
    <div style={{ width: "100%", maxWidth: 520 }}>
      {cartao && (
        <div style={{ margin: "10px 0" }}>
          <button
            type="button"
            onClick={pagarCartao}
            disabled={cartaoLoad}
            style={{ ...btn, opacity: cartaoLoad ? 0.6 : 1 }}
          >
            {cartaoLoad ? "Abrindo…" : `Pagar ${brl(cartao.valor)} no cartão`}
          </button>
          {cartaoMsg && <p style={{ color: "#c0392b" }}>{cartaoMsg}</p>}
        </div>
      )}

      {boletos.length > 0 && (
        <div style={{ margin: "10px 0" }}>
          <p style={{ fontWeight: "bold", marginBottom: 4 }}>Boletos</p>
          {boletos.map((p, i) => {
            const st = boletoState[p.parcid] || {};
            return (
              <div
                key={p.parcid}
                style={{ borderBottom: "1px solid #eee", padding: "8px 0" }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>Vence {dataBR(p.vencimento)}</span>
                  <strong>{brl(p.valor)}</strong>
                </div>
                {i === 0 && !st.linha && !st.err && (
                  <button
                    type="button"
                    onClick={() => gerarBoleto(p.parcid)}
                    disabled={st.load}
                    style={{ ...btn, marginTop: 6 }}
                  >
                    {st.load ? "Gerando…" : "Gerar boleto"}
                  </button>
                )}
                {st.linha && (
                  <p style={{ fontSize: "0.85em", wordBreak: "break-all" }}>
                    Linha digitável: <strong>{st.linha}</strong>
                    {st.url && (
                      <>
                        {" — "}
                        <a
                          href={st.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          abrir boleto
                        </a>
                      </>
                    )}
                  </p>
                )}
                {st.err && (
                  <p style={{ color: "#c0392b", fontSize: "0.85em" }}>
                    {st.err}
                  </p>
                )}
              </div>
            );
          })}
          {boletos.length > 1 && (
            <p style={{ fontSize: "0.85em", color: "#666" }}>
              Gere só o 1º boleto agora. As demais parcelas ficam disponíveis no
              portal do formando.
            </p>
          )}
        </div>
      )}

      {pixes.length > 0 && (
        <div style={{ margin: "10px 0" }}>
          <p style={{ fontWeight: "bold", marginBottom: 4 }}>
            Pix {pixes.length > 1 ? `(1ª de ${pixes.length})` : ""}
          </p>
          <GeraPix
            dados={{
              parctipo: "Contrato",
              parcconta: conta,
              parcid: pixes[0].parcid,
              parcvalor: pixes[0].valor,
            }}
            pix={pixCreds}
            mensagem={`Pague ${brl(pixes[0].valor)} com o QR abaixo (vence ${dataBR(
              pixes[0].vencimento
            )}).`}
          />
          {pixes.length > 1 && (
            <p style={{ fontSize: "0.85em", color: "#666" }}>
              As demais parcelas pix ficam disponíveis no portal do formando.
            </p>
          )}
        </div>
      )}

      <button type="button" onClick={onPronto} style={btn}>
        Concluir cadastro
      </button>
    </div>
  );
}

function CondicaoSelect({ aluno, formatura, baile, onPronto }) {
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [escolhida, setEscolhida] = useState(null);
  const [pixModo, setPixModo] = useState("qr");
  const [pixParcelas, setPixParcelas] = useState(2);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/condicoes/${formatura}?baile=${baile ? 1 : 0}`
        );
        if (!cancel) setLista((data && data.condicoes) || []);
      } catch (e) {
        if (!cancel) setErro("Não foi possível carregar as condições agora.");
      } finally {
        if (!cancel) setCarregando(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [formatura, baile]);

  const cond = lista.find((c) => c.id === escolhida) || null;

  async function confirmar() {
    if (!cond || enviando) return;
    setEnviando(true);
    setErro(null);
    const body = { aluno, condicao: cond.id, baile: baile ? 1 : 0 };
    if (cond.formato === "pix") {
      body.pix =
        pixModo === "parcelado"
          ? { modo: "parcelado", parcelas: Number(pixParcelas) }
          : { modo: "qr" };
    }
    try {
      const { data } = await api.post("/contrato/condicao", body);
      if (data && (data.ok || data.jaAplicada)) {
        setResultado(data);
      } else {
        setErro((data && data.erro) || "Não foi possível aplicar a condição.");
      }
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.erro;
      setErro(msg || "Não foi possível aplicar a condição. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  }

  const wrap = {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };
  const btn = {
    width: "100%",
    maxWidth: 480,
    padding: 12,
    marginTop: 12,
    cursor: "pointer",
  };

  if (carregando) {
    return (
      <div style={wrap}>
        <p>Carregando condições de pagamento…</p>
      </div>
    );
  }

  // condição aplicada -> ações de pagamento por formato (Fase B) + concluir
  if (resultado) {
    if (resultado.jaAplicada) {
      return (
        <div style={wrap}>
          <h1 style={{ textAlign: "center" }}>Condição já registrada ✅</h1>
          <p style={{ textAlign: "center", color: "#666" }}>
            Você já havia escolhido uma condição para este contrato. Acompanhe e
            pague pelo portal do formando.
          </p>
          <button type="button" onClick={onPronto} style={btn}>
            Concluir cadastro
          </button>
        </div>
      );
    }
    return (
      <div style={wrap}>
        <h1 style={{ textAlign: "center" }}>Condição registrada! ✅</h1>
        <p style={{ textAlign: "center", color: "#666" }}>
          Seu plano de pagamento foi gerado. Você pode pagar agora ou depois pelo
          portal do formando.
        </p>
        <PagamentoActions
          aluno={aluno}
          conta={resultado.conta}
          parcelas={resultado.parcelas || []}
          pix={resultado.pix}
          onPronto={onPronto}
        />
      </div>
    );
  }

  // sem condições configuradas -> não trava o formando
  if (!lista.length) {
    return (
      <div style={wrap}>
        <h1 style={{ textAlign: "center" }}>Tudo certo!</h1>
        <p style={{ textAlign: "center", color: "#666" }}>
          Ainda não há condições de pagamento configuradas para a sua turma.
          Sua comissão vai combinar o pagamento com você.
        </p>
        <button type="button" onClick={onPronto} style={btn}>
          Concluir cadastro
        </button>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={{ textAlign: "center" }}>
        <h1>Escolha sua forma de pagamento</h1>
        <p style={{ color: "#666" }}>
          Selecione uma condição para concluir. Esta etapa é obrigatória.
        </p>
      </div>

      <div style={{ width: "100%", maxWidth: 520 }}>
        {lista.map((c) => {
          const sel = c.id === escolhida;
          return (
            <div
              key={c.id}
              onClick={() => setEscolhida(c.id)}
              style={{
                border: sel ? "2px solid #0167ce" : "1px solid #ddd",
                borderRadius: 6,
                padding: 14,
                margin: "10px 0",
                cursor: "pointer",
                background: sel ? "#f0f7ff" : "#fff",
              }}
            >
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <strong>
                  {c.nome || FORMATO_LABEL[c.formato] || "Condição"}
                </strong>
                <strong>{brl(c.valor)}</strong>
              </div>
              <div style={{ fontSize: "0.9em", color: "#555" }}>
                {FORMATO_LABEL[c.formato] || c.formato} · {resumoCondicao(c)}
              </div>
              {c.descricao && (
                <div
                  style={{ fontSize: "0.85em", color: "#888", marginTop: 4 }}
                >
                  {c.descricao}
                </div>
              )}

              {sel && c.formato === "pix" && (
                <div
                  style={{ marginTop: 10, fontSize: "0.9em" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <label style={{ display: "block", margin: "4px 0" }}>
                    <input
                      type="radio"
                      name="pixmodo"
                      checked={pixModo === "qr"}
                      onChange={() => setPixModo("qr")}
                    />{" "}
                    Pagar via QR (valor sugerido: {brl(c.valor)})
                  </label>
                  <label style={{ display: "block", margin: "4px 0" }}>
                    <input
                      type="radio"
                      name="pixmodo"
                      checked={pixModo === "parcelado"}
                      onChange={() => setPixModo("parcelado")}
                    />{" "}
                    Parcelar em{" "}
                    <input
                      type="number"
                      min="1"
                      max={c.pixMax || 1}
                      value={pixParcelas}
                      disabled={pixModo !== "parcelado"}
                      onChange={(e) => setPixParcelas(e.target.value)}
                      style={{ width: 60 }}
                    />{" "}
                    x (até {c.pixMax || 1}x)
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {erro && (
        <p style={{ color: "#c0392b", maxWidth: 480, textAlign: "center" }}>
          {erro}
        </p>
      )}

      <button
        type="button"
        onClick={confirmar}
        disabled={!cond || enviando}
        style={{
          ...btn,
          cursor: !cond || enviando ? "not-allowed" : "pointer",
          opacity: !cond || enviando ? 0.6 : 1,
        }}
      >
        {enviando ? "Aplicando…" : "Confirmar condição"}
      </button>
    </div>
  );
}

export default () => {
  const location = useLocation();
  const assinatura = (location.state && location.state.assinatura) || null;
  const baile = (location.state && location.state.baile) || null;
  const aluno = (location.state && location.state.aluno) || null;
  const formatura = (location.state && location.state.formatura) || null;
  const email = (location.state && location.state.email) || "";
  const nome = (location.state && location.state.nome) || "";
  const signupHref = `/signup?email=${encodeURIComponent(email)}&name=${encodeURIComponent(nome)}`;

  // etapa do fluxo: principal -> (baile, se houver) -> condicao -> fim
  const semAssinatura = !assinatura || !assinatura.signerKey;
  const [etapa, setEtapa] = useState(semAssinatura ? "condicao" : "principal");
  const [principalFalhou, setPrincipalFalhou] = useState(false);
  const [baileAssinado, setBaileAssinado] = useState(false);

  // URL do PDF do contrato principal servido pela NOSSA API (inline; o portal
  // força download). Same-origin da API; funciona em dev e prod.
  const contratoUrl =
    assinatura && assinatura.envelopeId
      ? `${BASE_URL}/contrato/pdf/${assinatura.envelopeId}`
      : null;

  function concluirPrincipal() {
    setEtapa(baile && baile.disponivel ? "baile" : "condicao");
  }

  // 1) Cadastro completo (após escolher a condição). Como o e-mail já vai
  // travado na tela de criação de usuário, pulamos a tela-convite e mandamos
  // direto para o /signup (que pede só a senha e já loga no portal).
  if (etapa === "fim") {
    return <Redirect to={signupHref} />;
  }

  // 2) Escolha da condição (OBRIGATÓRIA) — após as assinaturas OU no fallback
  // sem widget / falha de carregamento (o cadastro já existe; a escolha
  // independe da assinatura). Só aplicamos se soubermos o aluno.
  if (etapa === "condicao" || principalFalhou) {
    if (!aluno || !formatura) {
      return (
        <Container>
          <Mensagem>
            <h1>Tudo certo! Você já está cadastrado no sistema.</h1>
            <p>Qualquer dúvida entre em contato com sua comissão.</p>
            <a className="auto" href={signupHref}>
              Aproveite para criar seu usuário em nosso portal. Clique aqui.
            </a>
          </Mensagem>
        </Container>
      );
    }
    return (
      <Container>
        <CondicaoSelect
          aluno={aluno}
          formatura={formatura}
          baile={baileAssinado ? 1 : 0}
          onPronto={() => setEtapa("fim")}
        />
      </Container>
    );
  }

  // 3) Etapa opcional do baile.
  if (etapa === "baile" && baile && baile.disponivel) {
    return (
      <EtapaBaile
        formId={baile.formId}
        principalEnvelopeId={assinatura.envelopeId}
        onConcluido={() => {
          setBaileAssinado(true);
          setEtapa("condicao");
        }}
        onPular={() => setEtapa("condicao")}
      />
    );
  }

  // etapa principal (obrigatória)
  return (
    <ContratoSign
      signerKey={assinatura.signerKey}
      pollEnvelopeId={assinatura.envelopeId}
      previewUrl={contratoUrl}
      titulo="Falta só assinar seu contrato."
      subtitulo="Leia o contrato abaixo e preencha seus dados para assinar."
      botaoLabel="Li e concordo — Assinar contrato"
      onConcluido={concluirPrincipal}
      onErroFatal={() => setPrincipalFalhou(true)}
    />
  );
};
